import i18n, { type TFunction } from 'i18next'

export const formatDuration = (
  passedTimeMs: number,
  tFunc?: TFunction,
  optionOverrides?: {
    allowMilliseconds?: boolean // default: false
    allowSeconds?: boolean // default: false
    relative?: boolean // default: true
  },
): string => {
  const options = {
    allowMilliseconds: false,
    allowSeconds: false,
    relative: true,
    ...optionOverrides,
  }

  const t = tFunc ?? i18n.t

  // 1min
  if (passedTimeMs < 60 * 1000) {
    if (options.allowMilliseconds && passedTimeMs < 1000) {
      if (!options.relative) {
        return t('common.time.milliseconds.long', {
          defaultValue: '{{passedTimeMs}} milliseconds',
          passedTimeMs: Math.floor(passedTimeMs),
          ns: 'shared',
        })
      }
      return t('common.time.milliseconds.relative-long', {
        defaultValue: '{{passedTimeMs}} milliseconds ago',
        passedTimeMs: Math.floor(passedTimeMs),
        ns: 'shared',
      })
    }

    if (options.allowSeconds) {
      if (!options.relative) {
        return t('common.time.seconds.long', {
          defaultValue: '{{passedTimeSeconds}} seconds',
          passedTimeSeconds: Math.floor(passedTimeMs / 1000),
          ns: 'shared',
        })
      }
      return t('common.time.seconds.relative-long', {
        defaultValue: '{{passedTimeSeconds}} seconds ago',
        passedTimeMinutes: Math.floor(passedTimeMs / (60 * 1000)),
        ns: 'shared',
      })
    }

    return t('common.time.just-now', { defaultValue: 'just now', ns: 'shared' })
  }

  // < 1h
  if (passedTimeMs < 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.minutes.long', {
        defaultValue: '{{passedTimeMinutes}} minutes',
        passedTimeMinutes: Math.floor(passedTimeMs / (60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.minutes.relative-long', {
      defaultValue: '{{passedTimeMinutes}} minutes ago',
      passedTimeMinutes: Math.floor(passedTimeMs / (60 * 1000)),
      ns: 'shared',
    })
  }

  // < 24h
  if (passedTimeMs < 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.hours.long', {
        defaultValue: '{{passedTimeHours}} hours',
        passedTimeHours: Math.floor(passedTimeMs / (60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.hours.relative-long', {
      defaultValue: '{{passedTimeHours}} hours ago',
      passedTimeHours: Math.floor(passedTimeMs / (60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  // < 7d
  if (passedTimeMs < 7 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.days.long', {
        defaultValue: '{{passedTimeDays}} days',
        passedTimeDays: Math.floor(passedTimeMs / (24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.days.relative-long', {
      defaultValue: '{{passedTimeDays}} days ago',
      passedTimeDays: Math.floor(passedTimeMs / (24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  // < 30d
  if (passedTimeMs < 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.weeks.long', {
        defaultValue: '{{passedTimeWeeks}} weeks',
        passedTimeWeeks: Math.floor(passedTimeMs / (7 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.weeks.relative-long', {
      defaultValue: '{{passedTimeWeeks}} weeks ago',
      passedTimeWeeks: Math.floor(passedTimeMs / (7 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  // < 12months
  if (passedTimeMs < 12 * 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.months.long', {
        defaultValue: '{{passedTimeMonths}} months',
        passedTimeMonths: Math.floor(passedTimeMs / (30 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.months.relative-long', {
      defaultValue: '{{passedTimeMonths}} months ago',
      passedTimeMonths: Math.floor(passedTimeMs / (30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  if (passedTimeMs < 10 * 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.years.long', {
        defaultValue: '{{passedTimeYears}} years',
        passedTimeYears: Math.floor(passedTimeMs / (12 * 30 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.years.relative-long', {
      defaultValue: '{{passedTimeYears}} years ago',
      passedTimeYears: Math.floor(passedTimeMs / (12 * 30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  if (passedTimeMs < 100 * 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.decades.long', {
        defaultValue: '{{passedTimeDecades}} decades',
        passedTimeDecades: Math.floor(passedTimeMs / (10 * 12 * 30 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.decades.relative-long', {
      defaultValue: '{{passedTimeDecades}} decades ago',
      passedTimeDecades: Math.floor(passedTimeMs / (10 * 12 * 30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  if (passedTimeMs < 1000 * 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.centuries.long', {
        defaultValue: '{{passedTimeCenturies}} centuries',
        passedTimeCenturies: Math.floor(passedTimeMs / (100 * 12 * 30 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.centuries.relative-long', {
      defaultValue: '{{passedTimeCenturies}} centuries ago',
      passedTimeCenturies: Math.floor(passedTimeMs / (100 * 12 * 30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  if (passedTimeMs < 1_000_000 * 30 * 24 * 60 * 60 * 1000) {
    if (!options.relative) {
      return t('common.time.millenia.long', {
        defaultValue: '{{passedTimeMillenia}} millenia',
        passedTimeMillenia: Math.floor(passedTimeMs / (1000 * 12 * 30 * 24 * 60 * 60 * 1000)),
        ns: 'shared',
      })
    }
    return t('common.time.millenia.relative-long', {
      defaultValue: '{{passedTimeMillenia}} millenia ago',
      passedTimeMillenia: Math.floor(passedTimeMs / (1000 * 12 * 30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }

  if (!options.relative) {
    return t('common.time.eons.long', {
      defaultValue: '{{passedTimeEons}} eons',
      passedTimeEons: Math.floor(passedTimeMs / (1_000_000 * 12 * 30 * 24 * 60 * 60 * 1000)),
      ns: 'shared',
    })
  }
  return t('common.time.eons.relative-long', {
    defaultValue: '{{passedTimeEons}} eons ago',
    passedTimeMillenia: Math.floor(passedTimeMs / (1_000_000 * 12 * 30 * 24 * 60 * 60 * 1000)),
    ns: 'shared',
  })
}
